import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { HiOutlineExclamation } from "react-icons/hi";
import sendEmail from "../../../../Common/email/sendEmail";
import { AdminGetUser } from "../../../../Common/lib/Admin/AdminAPI";
import { API } from "../../../../Common/lib/api";
import { getUserSession } from "../../../../Common/lib/Session";

/**
 * Displays the visualization approval confirmation modal
 * @returns Modal Component
 */
export default function AcceptModal({
  openAcceptModal,
  setOpenAcceptModal,
  visualizationRequest,
  setNotificationShow,
  setNotificationPurpose,
  setNotification,
  setRequests
}) {
  const [buttonEnabled, setButtonEnabled] = useState(true);

  /**
   * Handles visualization approval. Sends email to the publisher of the visualization informing them that their visualization has been approved.
   */
  async function acceptHandling() {
    try {
      setButtonEnabled(false);
      // Normalize project ID and retrieve the project
      let projectId = visualizationRequest.projectID;

      if (typeof projectId === "string" && projectId.startsWith("project-")) {
        try {
          const projectQuery = await API.projects.get(projectId, projectId);
          const projectObj = projectQuery.data.getProject;
          projectId = projectObj?.id || projectId.substring(8);
        } catch (e) {
          projectId = projectId.substring(8);
        }
      } else if (typeof projectId === "string" && projectId.length > 8) {
        // Legacy "project-" style IDs without the prefix
        projectId = projectId.substring(8);
      }

      const projectResult = await API.projects.get(projectId);
      const project = projectResult.data.getProject;

      if (!project) {
        throw new Error("Project not found for visualization approval");
      }

      let visualizations;
      try {
        visualizations = Array.isArray(project.dataVisualization)
          ? project.dataVisualization
          : JSON.parse(project.dataVisualization || "[]");
      } catch (e) {
        throw new Error("Invalid project visualizations payload");
      }

        // Get the ID and index of the visualization to be approved.
        const visualizationPayload = JSON.parse(visualizationRequest.visualization);
        const requestedVisualizationID = visualizationPayload.id;
        const approvedVisualizationIndex = visualizations.findIndex(
          (visualization) => visualization.id === requestedVisualizationID
        );

        if (approvedVisualizationIndex === -1) {
          throw new Error("Requested visualization not found on project");
        }

        // Change the approved visualiation "approved" property to true.
        visualizations[approvedVisualizationIndex].approved = true;

        let publisherUsername = visualizationRequest.userID;

        // Visualization info doesn't contain publisher's email so we need to retrieve it from Cognito
        const result = await AdminGetUser(publisherUsername);
        let userObject = {};

        // The result from Cognito has all the user attributes but they are objects in an array so it maps through the array and assigns each attrbute to a property on the userObject object
        result.UserAttributes.map((x) => {
          userObject[`${x.Name}`] = x.Value;
        });

        // Check that the destination email was populated
        if (userObject.email !== "" && userObject.email !== undefined) {
          let emailSubject = "Data Visualization Approved";
          let emailBody = `Hello ${
            userObject.given_name
          }, \n\nWe are pleased to inform you that your visualization titled "${
            JSON.parse(visualizationRequest.visualization).title
          }" attached to your research project "${
            visualizationRequest.projectTitle
          }" has been approved and is now available on our website. Thank you for adding your visualization and contributing to our platform. \n\nYou can find your visualization here: https://database.arcticfocus.org/metadata-display/${projectId}`;
          await sendEmail(userObject.email, emailSubject, emailBody, "Authorized");
        }
        
        const session = await getUserSession();
        await API.projects.update({
          id: projectId,
          userID: session.username,
          dataVisualization: JSON.stringify(visualizations),
        });

        // Mark the corresponding visualizationApproval log as approved
        try {
          await API.logs.update(visualizationRequest.id, { status: 'approved' });
        } catch (e) {
          // Best-effort; do not block approval if log update fails
        }

        // Log entry will be created automatically via signal when project is updated

        setRequests(prevRequests => {
          return prevRequests.filter(request => JSON.parse(request.visualization).id !== requestedVisualizationID);
        })

        setNotificationShow(true);
        setNotificationPurpose("Status");
        setNotification({
          status: true,
          title: "Data Visualization Approved",
          message: `The data visualization "${
            JSON.parse(visualizationRequest.visualization).title
          }" has been approved!`,
        });

        setOpenAcceptModal(false);
    } catch (error) {
      setButtonEnabled(true);
      setNotificationShow(true);
      setNotificationPurpose("Status");
      setNotification({
        status: false,
        title: "An error has occurred",
        message: `The data visualization could not be approved.`,
      });
    }
  }

  return (
    <Transition.Root show={openAcceptModal} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={setOpenAcceptModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                <div>
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                    <HiOutlineExclamation className="h-6 w-6 text-green-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                      Are you sure you want to approve this visualization?
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        This visualization will be publicly viewable through its projects metadata page.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6">
                  <button
                    type="button"
                    data-cy="approve-project"
                    className="mb-2 inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:bg-blue-200"
                    disabled={!buttonEnabled}
                    onClick={() => {
                      acceptHandling();
                    }}
                  >
                    {buttonEnabled ? 
                      (
                      "Approve Visualization"
                      ) : 
                      (
                        <>
                          <svg aria-hidden="true" class="w-5 h-5 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
                            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
                          </svg>
                          <span class="sr-only">Loading...</span>
                        </>
                      )
                    }
                  </button>
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-300 hover:bg-gray-400 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                    onClick={() => setOpenAcceptModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
